import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import Stack from '@mui/material/Stack';
import AccordionController from 'components/AccordionController';
import Section from 'components/Section';
import TextField from 'components/TextField';
import Form from 'components/Form';
import ChipInput from 'components/ChipInput';

import { useErrorHandler } from 'lib';
import { DepartureLocationEditForm_departureLocation$key } from '__generated__/DepartureLocationEditForm_departureLocation.graphql';
import { DepartureLocationEditFormMutation } from '__generated__/DepartureLocationEditFormMutation.graphql';

type FormValues = {
  name: string;
  codes: readonly string[];
};

type Props = {
  departureLocation: DepartureLocationEditForm_departureLocation$key;
};

const DepartureLocationEditForm: FC<Props> = ({ departureLocation }) => {
  const errorHandler = useErrorHandler();

  const { id, ...initial } = useFragment(
    graphql`
      fragment DepartureLocationEditForm_departureLocation on DepartureLocation {
        id
        name
        codes
      }
    `,
    departureLocation
  );

  const [update, loading] =
    useMutation<DepartureLocationEditFormMutation>(graphql`
      mutation DepartureLocationEditFormMutation(
        $id: ID!
        $input: UpdateDepartureLocationInput!
      ) {
        updateDepartureLocation(id: $id, input: $input) {
          ...EntitySidebar_entity
          ...DepartureLocationEditForm_departureLocation
        }
      }
    `);

  const handleSubmit = useCallback(
    (input: FormValues) => {
      update({
        variables: { id, input },
        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, id, update]
  );

  return (
    <Formik<FormValues> initialValues={initial} onSubmit={handleSubmit}>
      <Form loading={loading}>
        <AccordionController id="departureLocationEdit">
          <Section id="main" title="Bendra informacija">
            <Stack spacing={4}>
              <TextField autoFocus name="name" label="Pavadinimas" fullWidth />
              <ChipInput name="codes" label="Oro uostų kodai" />
            </Stack>
          </Section>
        </AccordionController>
      </Form>
    </Formik>
  );
};

export default DepartureLocationEditForm;
